import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { buildSegments, totalLength as polylineTotalLength } from '@/lib/elevationProfile/polylineMath';
import type { SmoothingLevel } from '@/lib/elevationProfile/profileFit';
import { loadProfiles, mergeLoaded, saveProfiles } from '@/lib/elevationProfile/profileStore';

/** A single (distance-along-line, elevation) pair. */
export type ProfileSample = { chainage: number; elevation: number };

export type ElevationProfile = {
  id: string;
  name: string;
  /** Pointcloud layer the user picked as the sample source. */
  sourceLayerId: string | null;
  /** Polyline vertices in project SRID (Z-up). */
  polylinePoints: [number, number, number][];
  /** Buffer width, metres. */
  bufferDistance: number;
  /** Sorted (by chainage) and downsampled samples. Empty until Compute is run. */
  samples: ProfileSample[];
  /** XY length of the polyline (metres). */
  totalLength: number;
  /** Raw point count before downsampling. Surfaced in the panel summary. */
  pointCount: number;
  createdAt: number;
  /** Show/hide in 3D scene + chart panel. */
  enabled: boolean;
  /**
   * Strength of the fitted ground line (`profileFit.ts`). Optional because
   * profiles saved before the fit existed have none; read it through
   * `DEFAULT_SMOOTHING`.
   */
  smoothing?: SmoothingLevel;
  /** Draw the raw samples behind the fitted line. Absent = shown. */
  showRawPoints?: boolean;
};

/**
 * A project's saved profiles, persisted to IndexedDB (`profileStore.ts`).
 *
 * The list starts empty and is filled from the store when first mounted.
 * Writes made before that load finishes are kept and merged over it, and
 * nothing is written back until the load has succeeded, so a write can't
 * replace the stored list with a partial one. If the store can't be read the
 * profiles still work for the session, they just aren't saved.
 */
export const elevationProfilesAtom = atomFamily((projectId: string) => {
  const base = atom<ElevationProfile[]>([]);
  let loaded = false;
  let dirty = false;
  let persistDisabled = false;

  const persist = (profiles: ElevationProfile[]) => {
    if (persistDisabled) return;
    saveProfiles(projectId, profiles).catch((err) => {
      console.error('[elevationProfile] could not save profiles', err);
    });
  };

  base.onMount = (setBase) => {
    if (loaded) return;
    loadProfiles(projectId).then(
      (stored) => {
        loaded = true;
        if (!dirty) {
          setBase(stored);
          return;
        }
        let merged: ElevationProfile[] = stored;
        setBase((current) => (merged = mergeLoaded(stored, current)));
        persist(merged);
      },
      (err) => {
        loaded = true;
        persistDisabled = true;
        console.error('[elevationProfile] could not load profiles', err);
      },
    );
  };

  return atom(
    (get) => get(base),
    (_get, set, next: ElevationProfile[]) => {
      set(base, next);
      if (loaded) persist(next);
      else dirty = true;
    },
  );
});

/**
 * Tool/panel state machine.
 *
 *   - 'closed'    → tool inactive, panel hidden.
 *   - 'drawing'   → user clicking vertices in the 3D scene.
 *   - 'panel'     → polyline finished; side-sheet open with form + chart.
 */
export type ElevationProfileToolPhase = 'closed' | 'drawing' | 'panel';

export interface ElevationProfileToolMode {
  phase: ElevationProfileToolPhase;
  /** ID of the draft / active profile being edited. */
  activeId: string | null;
}

export const DEFAULT_ELEVATION_PROFILE_TOOL_MODE: ElevationProfileToolMode = {
  phase: 'closed',
  activeId: null,
};

export const elevationProfileToolModeAtom = atom<ElevationProfileToolMode>(
  DEFAULT_ELEVATION_PROFILE_TOOL_MODE,
);

/**
 * Vertices placed during the in-progress draw, in project SRID.
 *
 * Lives in an atom (not local component state) so an out-of-Canvas hint
 * panel can read the count + render a "Done" button alongside the
 * in-Canvas drawing tool. Reset to `[]` when drawing ends — see
 * `ElevationProfileTool`'s phase-change effect.
 */
export const elevationProfileDraftPointsAtom = atom<[number, number, number][]>([]);

/**
 * Per-vertex layer-id of the surface that was picked when each vertex was
 * placed (parallel to `elevationProfileDraftPointsAtom`). `null` for hits
 * that came from a mesh rather than a point cloud. Used to pre-select the
 * "Source point cloud" dropdown in the panel — most users draw across one
 * cloud, so the most-frequent id is almost always the right default.
 */
export const elevationProfileDraftLayerIdsAtom = atom<(string | null)[]>([]);

/**
 * Pick the most-frequent non-null layer id from a list of per-vertex hits.
 * Returns `null` if there were no point-cloud hits at all.
 *
 * Ties go to the first id that reached the winning count, which biases
 * toward the layer the user picked from earliest. Acceptable: ties are
 * rare and the user can always change the selection in the panel.
 */
export function inferDominantLayerId(layerIds: ReadonlyArray<string | null>): string | null {
  const counts = new Map<string, number>();
  for (const id of layerIds) {
    if (id == null) continue;
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestCount = 0;
  for (const [id, count] of counts) {
    if (count > bestCount) {
      best = id;
      bestCount = count;
    }
  }
  return best;
}

/**
 * Chart-driven hover chainage. Set by the panel chart on mousemove; consumed
 * by `<SavedElevationProfiles>` to position a 3D dot on the polyline at the
 * hovered position. `null` = nothing hovered.
 */
export const hoveredProfileChainageAtom = atom<{
  profileId: string;
  chainage: number;
  /** Fitted ground elevation there; the 3D dot sits on it rather than on the
   *  drawn polyline's Z, which is wherever the user's clicks happened to land. */
  elevation?: number;
} | null>(null);

/**
 * Compute progress — written by `computeElevationProfile`'s onProgress
 * callback, read by the panel's progress bar. Mirrors the stockpile pattern.
 */
export interface ElevationProfileProgress {
  phase: 'loading' | 'sampling';
  done: number;
  total: number;
}

export const elevationProfileProgressAtom = atom<ElevationProfileProgress | null>(null);

// ── Mutators ────────────────────────────────────────────────────────────────

export const addElevationProfileAtom = atomFamily((projectId: string) =>
  atom(
    null,
    (
      get,
      set,
      input: {
        polylinePoints: [number, number, number][];
        bufferDistance: number;
        sourceLayerId: string | null;
      },
    ) => {
      const current = get(elevationProfilesAtom(projectId));
      const id = crypto.randomUUID();
      // Pre-compute the polyline length so it shows up in the panel + Layers
      // list immediately, before the user runs Compute. `pointCount === 0`
      // is what actually distinguishes "not yet sampled".
      const totalLength = polylineTotalLength(buildSegments(input.polylinePoints));
      const profile: ElevationProfile = {
        id,
        name: '',
        sourceLayerId: input.sourceLayerId,
        polylinePoints: input.polylinePoints,
        bufferDistance: input.bufferDistance,
        samples: [],
        totalLength,
        pointCount: 0,
        createdAt: Date.now(),
        enabled: true,
      };
      set(elevationProfilesAtom(projectId), [...current, profile]);
      return id;
    },
  ),
);

export const updateElevationProfileAtom = atomFamily((projectId: string) =>
  atom(null, (get, set, patch: { id: string } & Partial<Omit<ElevationProfile, 'id'>>) => {
    const current = get(elevationProfilesAtom(projectId));
    set(
      elevationProfilesAtom(projectId),
      current.map((p) => (p.id === patch.id ? { ...p, ...patch } : p)),
    );
  }),
);

export const deleteElevationProfileAtom = atomFamily((projectId: string) =>
  atom(null, (get, set, id: string) => {
    const current = get(elevationProfilesAtom(projectId));
    set(
      elevationProfilesAtom(projectId),
      current.filter((p) => p.id !== id),
    );
  }),
);

export const toggleElevationProfileAtom = atomFamily((projectId: string) =>
  atom(null, (get, set, id: string) => {
    const current = get(elevationProfilesAtom(projectId));
    set(
      elevationProfilesAtom(projectId),
      current.map((p) => (p.id === id ? { ...p, enabled: !p.enabled } : p)),
    );
  }),
);

export const renameElevationProfileAtom = atomFamily((projectId: string) =>
  atom(null, (get, set, payload: { id: string; name: string }) => {
    const current = get(elevationProfilesAtom(projectId));
    set(
      elevationProfilesAtom(projectId),
      current.map((p) => (p.id === payload.id ? { ...p, name: payload.name } : p)),
    );
  }),
);
